import { isGone, isNotFound, OperationOutcomeError } from '@medplum/core'
import type { SeenMedplumClient } from '@seen/core/client'
import type { JsonObject } from '@seen/core/json'
import type { AcceptReceipt } from '@seen/core/models/compositionNote'
import { ExtendedComposition } from '@seen/core/models/compositions/composition'
import { ExtendedCondition } from '@seen/core/models/conditions/ExtendedCondition'
import { ExtendedEncounter } from '@seen/core/models/encounters/encounter'
import {
  createReferenceVersionUrl,
  referenceFromReferenceString,
  referenceStringToId,
} from '@seen/core/references'
import { type Editor, generateJSON, type JSONContent } from '@tiptap/core'
import type { Node as PMNode, Schema } from '@tiptap/pm/model'
import type {
  AcceptApplyResult,
  AcceptSideEffectCtx,
  AcceptValidateCtx,
  AcceptValidateResult,
} from '#src/components/rich-text-editor/suggestionContracts.ts'
import {
  conditionInactiveDropReason,
  conditionMissingDropReason,
  conditionNotOnChartDropReason,
} from '#src/components/rich-text-editor/suggestionDropReasons.ts'

// Accept side effects for AI-suggested condition @-mentions (design §1.4, under
// the no-create product revision). The AI may only TAG EXISTING conditions — it
// never creates a Condition — so a condition-mention accept has exactly two
// durable effects: it links the tagged condition to the note's encounter
// (encounter.diagnosis), and it freezes the chip to a versioned reference. The
// versioned reference is the completed-state marker the accept pipeline's
// side-effect skip and the sign gate both read: an unversioned reference means
// the linkage has not finalized (R4 draft-flagged), a versioned one means it has.

// Link the tagged condition onto the note's encounter.diagnosis. Idempotent
// (link-if-absent, keyed on the condition id) so a resume/retry never
// double-links (R2 without a create). A composition with no encounter is a
// no-op — the versioned-reference freeze still finalizes the chip.
//
// Returns whether THIS call added the link — the ownership marker the receipt's
// createdRefs is contracted on. A link that already existed (a clinician linked
// the diagnosis before the accept, or a concurrent writer won) is NOT this
// accept's to remove on undo, so it must never read as accept-created. False
// negatives fail safe: a resume whose crashed predecessor already linked reads
// `false` and the undo then leaves that link in place — an extra link surviving
// an undo is recoverable, a pre-existing diagnosis silently unlinked is not.
async function linkConditionToEncounter(
  medplum: SeenMedplumClient,
  compositionRef: string,
  conditionRef: string
): Promise<boolean> {
  const composition = await medplum.readReferenceClass(
    ExtendedComposition,
    referenceFromReferenceString(compositionRef)
  )
  const encounterReference = composition.encounter
  if (!encounterReference?.reference) {
    // No encounter to link onto (e.g. a composition with no encounter). The
    // linkage half is a no-op; the versioned-reference freeze still finalizes
    // the chip.
    return false
  }
  const encounter = await medplum.readReferenceClass(
    ExtendedEncounter,
    encounterReference
  )
  if (isConditionLinked(encounter, conditionRef)) {
    return false
  }
  let added = false
  await medplum.patchResourceInstance(
    encounter,
    (enc) => {
      // Re-check inside the callback so a refetch-on-conflict retry stays
      // idempotent against a concurrent writer that linked it first. Reset the
      // marker each run: only the run that actually pushes the entry owns it.
      added = false
      if (isConditionLinked(enc, conditionRef)) {
        return
      }
      added = true
      enc.diagnosis = [
        ...enc.diagnosis,
        { condition: { reference: conditionRef } },
      ]
    },
    // The callback is intent-based with its whole conditional inside, so a 412
    // from another writer touching the encounter (a concurrent diagnosis link, a
    // check-out) retries against the fresh version instead of throwing into the
    // pipeline's swallowing catch and leaving the note's diagnosis silently
    // unlinked.
    //
    // It does knowingly depart from the option's "keep callbacks pure" caveat by
    // writing the `added` closure flag, which every other call site avoids. That
    // is safe only because of the reset at the top of each run: the callback
    // runs once per attempt and the persisted attempt is always the last one, so
    // `added` describes the run that was actually written. It matters because
    // `added` is the ownership marker for `createdRefs` — a false positive would
    // make a later undo unlink a diagnosis this accept never created.
    { __experimental_refetchOnConflict: true }
  )
  return added
}

function isConditionLinked(
  encounter: ExtendedEncounter,
  conditionRef: string
): boolean {
  const id = referenceStringToId(conditionRef)
  return encounter.diagnosis.some(
    (d) =>
      d.condition?.reference !== undefined &&
      referenceStringToId(d.condition.reference) === id
  )
}

// The condition-mention accept precondition, defined once (design State 1, DRY).
// Reads the tagged condition and confirms it belongs to this participant and is
// clinically current. Shared by the accept-time validation phase
// (validateConditionMentionAccept) and the side effect
// (applyConditionMentionAccept, which reuses the resolved condition for the
// versioned-reference freeze). A definite failure carries a clinical refusal
// reason; the caller decides whether that drops the accept (the validate phase)
// or draft-flags the node (the side effect, R4). `transient: true` marks a
// refusal whose cause is the READ failing (timeout, 5xx), not the precondition —
// only not-found/gone means the condition is actually missing, and reporting a
// network hiccup as "no longer in the chart" permanently dropped valid
// suggestions with a false clinical explanation.
type ConditionPrecondition =
  | { ok: true; condition: ExtendedCondition; unversionedRef: string }
  | { ok: false; reason: string; transient?: boolean }

async function checkConditionPrecondition(
  medplum: SeenMedplumClient,
  reference: string,
  // The mention's display text, so a refusal names the condition.
  display: string | undefined,
  // The note's participant (`Patient/{id}`); null skips the ownership check
  // (an editor with no participant in scope).
  patientRef: string | null
): Promise<ConditionPrecondition> {
  const unversionedRef = `Condition/${referenceStringToId(reference)}`
  let condition: ExtendedCondition
  try {
    condition = await medplum.readReferenceClass(
      ExtendedCondition,
      referenceFromReferenceString(unversionedRef)
    )
  } catch (error) {
    if (
      error instanceof OperationOutcomeError &&
      (isNotFound(error.outcome) || isGone(error.outcome))
    ) {
      return { ok: false, reason: conditionMissingDropReason(display) }
    }
    return {
      ok: false,
      reason: conditionMissingDropReason(display),
      transient: true,
    }
  }
  // Ownership BEFORE status: a wrong-chart condition's clinical status is
  // irrelevant, and the reason must name the real problem. The supply side
  // (getParticipantConditions) only ever offers this participant's conditions, so a
  // mismatch here is a hallucinated or miscopied id — the case this backstop
  // exists for, and the one whose accept would link another patient's condition
  // to this participant's encounter. A subject that is missing or not a Patient
  // cannot be verified as this participant's, so it refuses too.
  if (patientRef !== null) {
    const subject = condition.participantSubject?.reference
    if (
      subject === undefined ||
      referenceStringToId(subject) !== referenceStringToId(patientRef)
    ) {
      return { ok: false, reason: conditionNotOnChartDropReason(display) }
    }
  }
  if (!ExtendedCondition.isCurrentClinicalStatus(condition.clinicalStatus)) {
    return { ok: false, reason: conditionInactiveDropReason(display) }
  }
  return { ok: true, condition, unversionedRef }
}

// A mention node's display text, read defensively: `display` is a nullable
// attribute and these validators receive arbitrary node attrs.
function mentionDisplay(attrs: JsonObject): string | undefined {
  return typeof attrs.display === 'string' && attrs.display !== ''
    ? attrs.display
    : undefined
}

// validateOnAccept for the condition family (design State 1): the accept-time
// precondition check the pipeline runs on the to-be-inserted content BEFORE the
// insert, so a suggestion tagging a since-resolved/deleted condition is refused
// rather than committed. A reference-less (draft) mention has no existing
// condition to verify, so it passes (its accept is a no-op link). Shares the read
// with the side effect via checkConditionPrecondition — two reads per condition
// accept (validate, then apply), accepted as user-paced.
export async function validateConditionMentionAccept(
  attrs: JsonObject,
  ctx: AcceptValidateCtx
): Promise<AcceptValidateResult> {
  const reference = attrs.reference
  if (typeof reference !== 'string') {
    return { ok: true }
  }
  const check = await checkConditionPrecondition(
    ctx.medplum,
    reference,
    mentionDisplay(attrs),
    ctx.patientRef
  )
  if (check.ok) {
    return { ok: true }
  }
  return { ok: false, reason: check.reason, retryable: check.transient }
}

// applyOnAccept: validate the tagged condition exists and is clinically current
// (the shared precondition), link it to the encounter (idempotent), and return
// the versioned-reference patch. A missing or non-current condition leaves the
// node draft-flagged (R4): no patch, so the chip keeps its unversioned reference,
// the sign gate keeps blocking, and a later resume retries. `createdRef` is set
// only when the link call genuinely ADDED the encounter link — a pre-existing
// diagnosis link must never enter the receipt's createdRefs, or an undo of this
// accept would unlink a diagnosis the accept never created.
export async function applyConditionMentionAccept(
  attrs: JsonObject,
  ctx: AcceptSideEffectCtx
): Promise<AcceptApplyResult | null> {
  const reference = attrs.reference
  if (typeof reference !== 'string') {
    // A draftCondition-only mention has no runtime in 1.4a and the AI cannot
    // create conditions, so a reference-less mention has nothing to link.
    return null
  }
  const check = await checkConditionPrecondition(
    ctx.medplum,
    reference,
    mentionDisplay(attrs),
    ctx.patientRef
  )
  if (!check.ok) {
    return null
  }

  let linkAdded = false
  if (ctx.resourceRef) {
    linkAdded = await linkConditionToEncounter(
      ctx.medplum,
      ctx.resourceRef,
      check.unversionedRef
    )
  }

  const versionedRef = createReferenceVersionUrl(
    check.condition.reference,
    check.condition.versionId
  )
  const result: AcceptApplyResult = { patch: { reference: versionedRef } }
  if (linkAdded) {
    result.createdRef = versionedRef
  }
  return result
}

// compensate: unlink-only (R5). Nothing is ever created on the AI path, so
// compensation only removes the encounter-diagnosis links this accept added —
// never entered-in-error. Idempotent (remove-if-present). The refs to unlink are
// supplied by the undo-flip wiring (`ctx.linkedRefs`, read from the receipt and
// filtered to those no longer referenced by any content node), keyed on the
// condition id so the versioned receipt ref matches the unversioned encounter
// link.
export async function compensateConditionMentionAccept(
  ctx: AcceptSideEffectCtx
): Promise<void> {
  if (!ctx.resourceRef || !ctx.linkedRefs || ctx.linkedRefs.length === 0) {
    return
  }
  const conditionIds = new Set(
    ctx.linkedRefs.map((ref) => referenceStringToId(ref))
  )
  const composition = await ctx.medplum.readReferenceClass(
    ExtendedComposition,
    referenceFromReferenceString(ctx.resourceRef)
  )
  const encounterReference = composition.encounter
  if (!encounterReference?.reference) {
    return
  }
  const encounter = await ctx.medplum.readReferenceClass(
    ExtendedEncounter,
    encounterReference
  )
  const matches = (d: { condition?: { reference?: string } }): boolean =>
    d.condition?.reference !== undefined &&
    conditionIds.has(referenceStringToId(d.condition.reference))
  if (!encounter.diagnosis.some(matches)) {
    return
  }
  await ctx.medplum.patchResourceInstance(encounter, (enc) => {
    enc.diagnosis = enc.diagnosis.filter((d) => !matches(d))
  })
}

// The pre-insert analog of collectReferencedConditionIds: the condition ids a
// suggestion's UNINSERTED content references, read before it becomes a doc node.
// Reveal-time validation reads what paint reads — a suggestion's resolved
// htmlContent, sourced from record.op — so it must parse the serialized HTML
// through the editor schema (the same generateJSON walk validateSuggestionContent
// uses at collectCustomNodes) rather than descend a live PMNode. Matched
// version-agnostically by id, identical to the doc-walk counterpart, so an
// unversioned AI-insert reference and a versioned chip reconcile the same way.
export function collectHtmlReferencedConditionIds(
  editor: Editor,
  html: string
): Set<string> {
  return new Set(collectHtmlReferencedConditions(editor, html).keys())
}

// The same walk, keeping each referenced condition's display text so a
// reveal-time refusal can name the condition the way the accept-time one does
// (which reads the display straight off the node attrs it validates). A mention
// with no display maps to undefined; the copy falls back to "This condition".
export function collectHtmlReferencedConditions(
  editor: Editor,
  html: string
): Map<string, string | undefined> {
  const byId = new Map<string, string | undefined>()
  const visit = (node: JSONContent): void => {
    const reference = node.attrs?.reference
    if (typeof reference === 'string' && reference.startsWith('Condition/')) {
      const display = node.attrs?.display
      byId.set(
        referenceStringToId(reference),
        typeof display === 'string' && display !== '' ? display : undefined
      )
    }
    node.content?.forEach(visit)
  }
  visit(parseHtml(editor, html))
  return byId
}

// generateJSON rebuilds the editor's whole schema on every call, and the
// reconciler asks a presence question for every persisted record on every
// document transaction — i.e. per keystroke. An op's content is immutable, so
// the parse is cached per schema. Callers only read the tree and derive their
// own result from it, so the shared value is never mutated.
const parsedHtmlCache = new WeakMap<Schema, Map<string, JSONContent>>()

function parseHtml(editor: Editor, html: string): JSONContent {
  let bySchema = parsedHtmlCache.get(editor.schema)
  if (!bySchema) {
    bySchema = new Map()
    parsedHtmlCache.set(editor.schema, bySchema)
  }
  const cached = bySchema.get(html)
  if (cached) {
    return cached
  }
  const parsed = generateJSON(
    html,
    editor.extensionManager.extensions
  ) as JSONContent
  bySchema.set(html, parsed)
  return parsed
}

// The condition ids every content node in the doc currently references (a
// condition mention carries `Condition/<id>[/_history/<v>]` on `reference`).
// Matched version-agnostically by id so a versioned chip and an unversioned
// receipt/encounter ref reconcile.
// `exclude` skips matching nodes, so a caller can ask the same question about
// everything EXCEPT a given suggestion's own chips.
export function collectReferencedConditionIds(
  doc: PMNode,
  exclude?: (node: PMNode) => boolean
): Set<string> {
  const ids = new Set<string>()
  doc.descendants((node) => {
    const reference = node.attrs?.reference
    if (
      typeof reference === 'string' &&
      reference.startsWith('Condition/') &&
      !exclude?.(node)
    ) {
      ids.add(referenceStringToId(reference))
    }
  })
  return ids
}

// Undo-of-accept unlink set (R5): the accept's created refs whose condition is
// NO LONGER referenced by any content node still in the doc. This is the filter
// AcceptSideEffectCtx.linkedRefs is contracted on — it keeps the link a sibling
// suggestion still tags (two accepts on the same condition, undo one) and
// unlinks only what this undo genuinely orphaned.
export function orphanedConditionRefs(
  createdRefs: Record<string, string> | undefined,
  doc: PMNode
): string[] {
  if (!createdRefs) {
    return []
  }
  const referenced = collectReferencedConditionIds(doc)
  return Object.values(createdRefs).filter(
    (reference) => !referenced.has(referenceStringToId(reference))
  )
}

// The committed-accept condition refs that a mount-time Medplum->Liveblocks
// reconcile would strand: a condition a committed accept receipt linked that the
// LIVE (synchronized) room doc still shows as a chip, but the INCOMING Medplum
// content has dropped. When this is non-empty the incoming copy is BEHIND the
// room (e.g. the accept's 2.5s debounced content save was killed before it
// flushed), so overwriting the doc with it would remove the committed chip; the
// reconcile sweep / pre-sign drain would then read that receipt's link as an
// orphan (orphanedConditionRefs > 0) and destructively unlink Encounter.diagnosis.
// The mount reconcile skips when this returns anything. Version-agnostic id match
// mirrors orphanedConditionRefs so a versioned chip and an unversioned receipt ref
// reconcile.
export function committedAcceptRefsDroppedByIncoming(
  editor: Editor,
  incomingHtml: string,
  receipts: (AcceptReceipt | undefined)[]
): string[] {
  const liveIds = collectReferencedConditionIds(editor.state.doc)
  const incomingIds = collectHtmlReferencedConditionIds(editor, incomingHtml)
  const dropped: string[] = []
  for (const receipt of receipts) {
    if (!receipt?.createdRefs) {
      continue
    }
    for (const reference of Object.values(receipt.createdRefs)) {
      const id = referenceStringToId(reference)
      if (liveIds.has(id) && !incomingIds.has(id)) {
        dropped.push(reference)
      }
    }
  }
  return dropped
}

// Does a chip belong to a given suggestion's accept? Its stamp id is
// `${suggestionId}` or `${suggestionId}:${ordinal}` (see aiSuggestionStamp).
export function acceptIdBelongsTo(
  acceptId: string | null | undefined,
  suggestionId: string
): boolean {
  return (
    typeof acceptId === 'string' &&
    (acceptId === suggestionId || acceptId.startsWith(`${suggestionId}:`))
  )
}

// The condition ids referenced by chips that do NOT belong to `suggestionId` — a
// sibling accept's chip or a manually-typed mention. Used to verify that a
// still-referenced (non-orphaned) created link is genuinely owned by someone
// OTHER than this suggestion before clearing this receipt, so this suggestion's
// own lingering chip can never be mistaken for a sibling and strand the link.
function conditionIdsReferencedByOthers(
  doc: PMNode,
  suggestionId: string
): Set<string> {
  return collectReferencedConditionIds(doc, (node) =>
    acceptIdBelongsTo(node.attrs?.acceptId, suggestionId)
  )
}

// True when every ref this receipt created is still referenced by a chip that is
// NOT this suggestion's — the verified sibling-owned case where clearing the
// receipt (without a FHIR unlink) is safe because a sibling keeps the link.
function siblingOwnsAllCreatedRefs(
  receipt: AcceptReceipt,
  doc: PMNode,
  suggestionId: string
): boolean {
  const siblingIds = conditionIdsReferencedByOthers(doc, suggestionId)
  return Object.values(receipt.createdRefs ?? {}).every((ref) =>
    siblingIds.has(referenceStringToId(ref))
  )
}

// The undo-of-accept compensation decision (R5), extracted from CollaborativeEditor
// so the wiring — not just the orphan filter — is integration-testable. When the
// reconciler flips an accepted record back to pending on an undo: unlink only the
// conditions this accept linked that no remaining content node still references
// (orphanedConditionRefs), then clear the receipt refs; when NOTHING is orphaned
// (each condition is still referenced by a sibling chip whose receipt owns the
// link) clear the receipt refs WITHOUT a FHIR unlink — not a silent drop of an
// un-compensated link; on unlink failure leave the receipt intact so the sign
// gate's orphan half still blocks and a later compensation can retry.
export async function runUndoFlipCompensation(deps: {
  medplum: SeenMedplumClient
  suggestionId: string
  resourceRef: string | null
  doc: PMNode
  receipt: AcceptReceipt | undefined
  clearReceiptRefs: (id: string) => void | Promise<void>
}): Promise<void> {
  const { receipt } = deps
  if (!receipt?.createdRefs || Object.keys(receipt.createdRefs).length === 0) {
    return
  }
  const orphanedRefs = orphanedConditionRefs(receipt.createdRefs, deps.doc)
  if (orphanedRefs.length === 0) {
    // Nothing orphaned: every created ref's condition is still referenced by a
    // live chip. Clear this receipt's refs ONLY when a VERIFIED SIBLING owns the
    // link — a chip that does NOT belong to this suggestion (a sibling accept, or
    // a manually-typed mention). If the only live reference is THIS suggestion's
    // own lingering chip, the accept is not truly undone: clearing would strand
    // the link on an emptied receipt the reconcile sweep can no longer heal
    // (orphan detection keys off createdRefs), so leave the receipt intact.
    if (siblingOwnsAllCreatedRefs(receipt, deps.doc, deps.suggestionId)) {
      await deps.clearReceiptRefs(deps.suggestionId)
    }
    return
  }
  try {
    await compensateConditionMentionAccept({
      medplum: deps.medplum,
      suggestionId: deps.suggestionId,
      nodeId: receipt.nodeIds[0] ?? deps.suggestionId,
      resourceRef: deps.resourceRef,
      // Compensation unlinks refs the receipt already owns; it never validates
      // ownership, so the scoping input is deliberately absent.
      patientRef: null,
      linkedRefs: orphanedRefs,
    })
    await deps.clearReceiptRefs(deps.suggestionId)
  } catch {
    // Leave the receipt intact: the sign gate's orphan half backstops the
    // still-linked condition, and a later compensation can retry.
  }
}
